import React, { FC, useRef, useState } from 'react';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Modal,
  H1,
  Input,
  Form,
  FormItem,
  TextArea,
  Upload,
  UploadFile,
} from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import { submitProblem } from './state/reducer';
import { hasValidationErrors } from '../AddNewPositionWizard/helpers';
import { ProblemReportModalProps, ProblemReportModalForm } from './types';
import {
  normalizeFile,
  getValidationRules,
  getSubmitProblemData,
  invalidateOkButton,
} from './helpers';

const { FormattedMessage, injectIntl } = Intl;

const ProblemReportModal: FC<ProblemReportModalProps &
  Intl.InjectedIntlProps> = props => {
  const { submitProblem, intl } = props;
  const history = useHistory();
  const formRef = useRef<WrappedFormUtils>();
  const [okDisabled, setOkDisabled] = useState<boolean>(false);
  const goBack = () => history.goBack();

  const handleSubmit = () => {
    const form = formRef.current;
    if (!form) return;
    form.validateFields();
    if (!hasValidationErrors(form.getFieldsError())) {
      const values = form.getFieldsValue() as ProblemReportModalForm;
      if (!values) return;
      const data = getSubmitProblemData(values);
      submitProblem(data);
      goBack();
    }
  };

  const uploadValueChanged = (files: UploadFile[]) => {
    if (!files) return;
    invalidateOkButton(files, setOkDisabled);
  };

  const rules = getValidationRules(intl);
  const pageTitle = (
    <H1 color="headerTertiary">
      {<FormattedMessage id={messageIds.vms.reportProblem.pageTitle} />}
    </H1>
  );
  const title = (
    <H1>
      <FormattedMessage id={messageIds.vms.reportProblem.title} />
    </H1>
  );

  return (
    <Modal
      pageTitle={pageTitle}
      title={title}
      visible={true}
      closable={true}
      onCancel={goBack}
      onOk={handleSubmit}
      okButtonProps={{ disabled: okDisabled, type: 'primary' }}
      destroyOnClose
      okText={<FormattedMessage id={messageIds.vms.reportProblem.okText} />}
    >
      <Form
        onError={alert}
        onValidFieldsSubmit={alert}
        wrappedComponentRef={formRef}
      >
        <FormItem
          id={nameof<ProblemReportModalForm>(x => x.subject)}
          label={<FormattedMessage id={messageIds.vms.reportProblem.subject} />}
          hasFeedback
          options={{
            rules: [
              {
                required: true,
              },
            ],
          }}
        >
          <Input />
        </FormItem>
        <FormItem
          id={nameof<ProblemReportModalForm>(x => x.description)}
          label={
            <FormattedMessage id={messageIds.vms.reportProblem.description} />
          }
          hasFeedback
          options={{
            rules: [
              {
                required: true,
              },
            ],
          }}
        >
          <TextArea autoSize={{ minRows: 10 }} />
        </FormItem>
        <FormItem
          id={nameof<ProblemReportModalForm>(x => x.upload)}
          options={{
            valuePropName: 'fileList',
            getValueFromEvent: normalizeFile,
            getValueProps: uploadValueChanged,
            rules: rules,
          }}
        >
          <Upload>
            <FormattedMessage id={messageIds.vms.reportProblem.attachFiles} />
          </Upload>
        </FormItem>
      </Form>
    </Modal>
  );
};

export default injectIntl(
  connect(() => ({}), { submitProblem })(ProblemReportModal),
);
